import { useState } from 'react';
import { Mail, MapPin, PackageSearch, Pencil, Phone, Plus, Trash2, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useDeleteSupplier, useSuppliers } from '@/api/hooks/useSuppliers';
import type { Supplier } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { SupplierFormDialog } from '@/features/suppliers/SupplierFormDialog';

export function SuppliersPage() {
  const { t } = useTranslation();
  const { data: suppliers, isLoading } = useSuppliers();
  const deleteSupplier = useDeleteSupplier();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null);

  function openCreate() {
    setEditing(undefined);
    setFormOpen(true);
  }

  function openEdit(supplier: Supplier) {
    setEditing(supplier);
    setFormOpen(true);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteSupplier.mutateAsync(deleteTarget.id);
      toast({ title: 'Supplier deleted', variant: 'success' });
      setDeleteTarget(null);
    } catch (error) {
      // SUPPLIER_IN_USE is the common case — the server names the exact count
      // of medicines still linked, which is more useful than a generic toast.
      toast({
        title: 'Could not delete supplier',
        description: error instanceof ApiError ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">{t('suppliersTitle')}</h1>
          <p className="text-sm text-muted-foreground">
            {suppliers ? `${suppliers.length} supplier(s)` : 'Loading…'}
          </p>
        </div>
        <Can permission={PERMISSIONS.suppliersManage}>
          <Button onClick={openCreate}>
            <Plus /> {t('addSupplier')}
          </Button>
        </Can>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
      ) : !suppliers || suppliers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <Truck className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No suppliers yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {suppliers.map((supplier) => (
            <Card key={supplier.id} className="flex flex-col p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <p className="min-w-0 truncate font-medium">{supplier.name}</p>
                <Badge variant="secondary" className="shrink-0 gap-1">
                  <PackageSearch className="h-3 w-3" /> {supplier.medicineCount}
                </Badge>
              </div>

              <div className="mb-3 flex-1 space-y-1 text-xs text-muted-foreground">
                <p className="flex items-center gap-1.5">
                  <Phone className="h-3 w-3 shrink-0" /> {supplier.contact}
                </p>
                {supplier.email && (
                  <p className="flex items-center gap-1.5">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="truncate">{supplier.email}</span>
                  </p>
                )}
                {supplier.address && (
                  <p className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{supplier.address}</span>
                  </p>
                )}
              </div>

              <Can permission={PERMISSIONS.suppliersManage}>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(supplier)}>
                    <Pencil /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-status-expired hover:bg-status-expired/10 hover:text-status-expired"
                    onClick={() => setDeleteTarget(supplier)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </Can>
            </Card>
          ))}
        </div>
      )}

      <SupplierFormDialog supplier={editing} open={formOpen} onOpenChange={setFormOpen} />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.name ?? 'this supplier'}?`}
        description="This can't be undone. Suppliers still linked to medicines can't be deleted — reassign those first."
        confirmLabel={t('delete')}
        pending={deleteSupplier.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}